import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, Trash2, Clock, AlertCircle, Lock, Mail, Eye, EyeOff } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { cartAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';

interface CartItem {
  id: number;
  program: number;
  program_name: string;
  program_payment_type?: 'monthly' | 'one_time';
  program_payment_label?: string;
  program_image: string | null;
  level_name: string | null;
  child_name: string;
  child_data: any;
  unit_price: string;
  line_total: string;
}

interface CartData {
  id: number;
  items: CartItem[];
  total_items: number;
  subtotal: string;
  expires_at: string | null;
  seconds_remaining: number | null;
  is_expired: boolean;
}

interface CartSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const CartSidebar: React.FC<CartSidebarProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated, login, register } = useAuthStore();
  
  // Auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [authLoading, setAuthLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Timer state
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  // Fetch cart data
  const { data: cart, isLoading, refetch } = useQuery<CartData>(
    'cart-sidebar',
    async () => {
      const response = await cartAPI.getCart();
      return response.data;
    },
    {
      enabled: isOpen,
      refetchInterval: isOpen ? 10000 : false, // Refetch every 10 seconds when open
      onSuccess: (data) => {
        if (data.seconds_remaining !== null) {
          setSecondsLeft(data.seconds_remaining);
        }
      }
    }
  );

  // Countdown timer effect
  useEffect(() => {
    if (!isOpen || secondsLeft === null || secondsLeft <= 0) return;

    const timer = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev === null || prev <= 0) {
          clearInterval(timer);
          // Refetch to confirm expiration
          refetch();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, secondsLeft, refetch]);

  // Remove item mutation
  const removeItemMutation = useMutation({
    mutationFn: (itemId: number) => cartAPI.removeItem(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries('cart-sidebar');
      queryClient.invalidateQueries('cart-count');
      toast.success('Item removed from cart');
    },
    onError: () => {
      toast.error('Failed to remove item');
    }
  });

  // Clear cart mutation
  const clearCartMutation = useMutation({
    mutationFn: () => cartAPI.clearCart(),
    onSuccess: () => {
      queryClient.invalidateQueries('cart-sidebar');
      queryClient.invalidateQueries('cart-count');
      toast.success('Cart cleared');
      onClose();
    }
  });

  // Format time remaining
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle checkout
  const handleCheckout = () => {
    onClose();
    navigate('/cart');
  };

  // Handle auth form submit
  const handleAuthSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    setAuthLoading(true);

    try {
      if (authMode === 'login') {
        const email = formData.get('email') as string;
        const password = formData.get('password') as string;
        await login(email, password);
        toast.success('Logged in successfully!');
        setShowAuthModal(false);
        // Refetch cart to merge any guest cart items
        queryClient.invalidateQueries('cart-sidebar');
        queryClient.invalidateQueries('cart-count');
        // Navigate to cart for checkout
        onClose();
        navigate('/cart');
      } else {
        const data = {
          email: formData.get('email') as string,
          password: formData.get('password') as string,
          password2: formData.get('password2') as string,
          first_name: formData.get('first_name') as string,
          last_name: formData.get('last_name') as string,
          phone: formData.get('phone') as string,
        };
        
        if (data.password !== data.password2) {
          toast.error('Passwords do not match.');
          setAuthLoading(false);
          return;
        }
        
        await register(data);
        toast.success('Account created! Please check your email to verify.');
        setShowAuthModal(false);
        queryClient.invalidateQueries('cart-sidebar');
        queryClient.invalidateQueries('cart-count');
        onClose();
        navigate('/cart');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || error.response?.data?.email?.[0] || 'Authentication failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const isExpired = secondsLeft !== null && secondsLeft <= 0;
  const isLowTime = secondsLeft !== null && secondsLeft <= 60 && secondsLeft > 0;

  return (
    <>
      {/* Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40"
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-primary-500 to-primary-600 text-white">
              <div className="flex items-center">
                <ShoppingCart size={24} className="mr-2" />
                <h2 className="text-xl font-bold">Your Cart</h2>
                {cart && cart.total_items > 0 && (
                  <span className="ml-2 bg-white text-primary-600 text-sm font-bold px-2 py-0.5 rounded-full">
                    {cart.total_items}
                  </span>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Timer Banner */}
            {cart && cart.total_items > 0 && secondsLeft !== null && (
              <div className={`p-3 flex items-center justify-center ${
                isExpired ? 'bg-red-100 text-red-700' : 
                isLowTime ? 'bg-amber-100 text-amber-700' : 
                'bg-blue-50 text-blue-700'
              }`}>
                <Clock size={18} className="mr-2" />
                {isExpired ? (
                  <span className="font-medium">Cart expired! Items have been cleared.</span>
                ) : (
                  <span className="font-medium">
                    Reserved for: <span className="font-bold">{formatTime(secondsLeft)}</span>
                  </span>
                )}
              </div>
            )}

            {/* Cart Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {isLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                </div>
              ) : !cart || cart.total_items === 0 || isExpired ? (
                <div className="text-center py-12">
                  <ShoppingCart size={64} className="mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-600 mb-2">
                    {isExpired ? 'Your cart has expired' : 'Your cart is empty'}
                  </h3>
                  <p className="text-gray-500 mb-6">
                    {isExpired 
                      ? 'Items are held for 5 minutes. Please add them again.'
                      : 'Add programs to your cart to get started!'
                    }
                  </p>
                  <Link
                    to="/programs"
                    onClick={onClose}
                    className="btn-primary inline-block"
                  >
                    Browse Programs
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {cart.items.map((item) => (
                    <div
                      key={item.id}
                      className="bg-gray-50 rounded-lg p-4 relative"
                    >
                      <button
                        onClick={() => removeItemMutation.mutate(item.id)}
                        className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 transition-colors"
                        disabled={removeItemMutation.isLoading}
                      >
                        <Trash2 size={18} />
                      </button>
                      
                      <div className="flex gap-3">
                        {item.program_image && (
                          <img
                            src={item.program_image}
                            alt={item.program_name}
                            className="w-16 h-16 rounded-lg object-cover"
                          />
                        )}
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{item.program_name}</h4>
                          {item.level_name && (
                            <p className="text-sm text-gray-500">{item.level_name}</p>
                          )}
                          <p className="text-sm text-primary-600 font-medium mt-1">
                            Child: {item.child_name}
                          </p>
                          <p className="text-lg font-bold text-gray-900 mt-2">
                            ${item.unit_price}{item.program_payment_type === 'one_time' ? ' one time' : '/mo'}
                          </p>
                          {item.program_payment_type === 'one_time' && (
                            <p className="text-xs text-amber-700">No monthly subscription</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Clear Cart Button */}
                  <button
                    onClick={() => clearCartMutation.mutate()}
                    className="w-full text-sm text-red-600 hover:text-red-700 py-2"
                    disabled={clearCartMutation.isLoading}
                  >
                    Clear Cart
                  </button>
                </div>
              )}
            </div>

            {/* Footer */}
            {cart && cart.total_items > 0 && !isExpired && (
              <div className="border-t p-4 bg-gray-50">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="text-2xl font-bold text-gray-900">${cart.subtotal}</span>
                </div>
                <button
                  onClick={handleCheckout}
                  className="w-full btn-primary py-3 text-lg min-h-[48px]"
                >
                  Proceed to Checkout
                </button>
                {!isAuthenticated && (
                  <p className="text-center text-sm text-gray-500 mt-2">
                    Checkout as a guest, or <Link to="/login" className="text-primary-600 hover:underline">sign in</Link> for your parent dashboard.
                  </p>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Auth Modal */}
      <AnimatePresence>
        {showAuthModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
            onClick={() => setShowAuthModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">
                    {authMode === 'login' ? 'Welcome Back' : 'Create Account'}
                  </h3>
                  <button
                    onClick={() => setShowAuthModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <X size={20} />
                  </button>
                </div>

                <form onSubmit={handleAuthSubmit} className="space-y-4">
                  {authMode === 'register' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          First Name
                        </label>
                        <input
                          type="text"
                          name="first_name"
                          required
                          className="input-field w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Last Name
                        </label>
                        <input
                          type="text"
                          name="last_name"
                          required
                          className="input-field w-full"
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <div className="relative">
                      <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        required
                        className="input-field w-full pl-10"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  {authMode === 'register' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        className="input-field w-full"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <div className="relative">
                      <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        required
                        className="input-field w-full pl-10 pr-10"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  {authMode === 'register' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Confirm Password
                      </label>
                      <input
                        type="password"
                        name="password2"
                        required
                        className="input-field w-full"
                        placeholder="••••••••"
                      />
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={authLoading}
                    className="w-full btn-primary py-3"
                  >
                    {authLoading ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Processing...
                      </span>
                    ) : authMode === 'login' ? (
                      'Sign In'
                    ) : (
                      'Create Account'
                    )}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  {authMode === 'login' ? (
                    <p className="text-gray-600">
                      Don't have an account?{' '}
                      <button
                        onClick={() => setAuthMode('register')}
                        className="text-primary-600 font-semibold hover:underline"
                      >
                        Sign up
                      </button>
                    </p>
                  ) : (
                    <p className="text-gray-600">
                      Already have an account?{' '}
                      <button
                        onClick={() => setAuthMode('login')}
                        className="text-primary-600 font-semibold hover:underline"
                      >
                        Sign in
                      </button>
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CartSidebar;
